import { httpClient } from '../funcs'
import { saveOAuth2Token } from './saveToken'

// Интерфейсы для типизации
export interface OAuth2Tokens {
    access_token: string
    refresh_token?: string
}

export type SaveOAuth2Options = {
    /** Партнёр / саппорт-URL: refresh остаётся в LS. Кабинет — нет. */
    persistRefresh?: boolean
}

type StoredOAuth2 = {
    access_token?: string
    refresh_token?: string
    persist_refresh?: boolean
}

/** Access только в памяти вкладки. LS: `{}` (флаг cookie-сессии) или `{refresh_token}` у партнёра. */
let memoryAccessToken: string | null = null

export function setMemoryAccessToken(token: string | null): void {
    memoryAccessToken = token
}

export function getMemoryAccessToken(): string | null {
    return memoryAccessToken
}

export function oauth2LocalStorageValue(
    tokens: OAuth2Tokens,
    persistRefresh = false,
): string {
    if (persistRefresh && tokens.refresh_token) {
        return JSON.stringify({
            refresh_token: tokens.refresh_token,
            persist_refresh: true,
        })
    }
    return JSON.stringify({})
}

export interface TokenRefreshResponse {
    access_token: string
    refresh_token: string
}

// Ключ localStorage: флаг OAuth2-сессии (+ refresh у партнёра/саппорта)
const TOKEN_KEY = 'token'

function parseTokenKey(): StoredOAuth2 | null {
    try {
        const tokenData = localStorage.getItem(TOKEN_KEY)
        if (!tokenData) {
            return null
        }
        const parsed = JSON.parse(tokenData)
        return parsed && typeof parsed === 'object' ? parsed : null
    } catch (error) {
        console.error('Failed to parse OAuth2 tokens from storage:', error)
        return null
    }
}

/** Партнёр/саппорт: в LS явный persist_refresh. Старый кабинетный refresh без флага — нет. */
export function isPersistRefreshSession(): boolean {
    hydrateAccessFromLocalStorage()
    return parseTokenKey()?.persist_refresh === true
}

/** Старые вкладки клали access в LS — забираем в память и вычищаем. */
function hydrateAccessFromLocalStorage(): void {
    const parsed = parseTokenKey()
    if (!parsed?.access_token) {
        return
    }
    if (!memoryAccessToken) {
        memoryAccessToken = parsed.access_token
    }
    const next: StoredOAuth2 = {}
    if (parsed.refresh_token) {
        next.refresh_token = parsed.refresh_token
    }
    if (parsed.persist_refresh === true) {
        next.persist_refresh = true
    }
    localStorage.setItem(TOKEN_KEY, JSON.stringify(next))
}

/**
 * Access из памяти, refresh из LS (партнёр/саппорт).
 */
export function getOAuth2TokensFromStorage(): OAuth2Tokens | null {
    hydrateAccessFromLocalStorage()
    const parsed = parseTokenKey()
    if (!parsed && !memoryAccessToken) {
        return null
    }
    const result: OAuth2Tokens = {
        access_token: memoryAccessToken || '',
    }
    if (parsed?.refresh_token) {
        result.refresh_token = parsed.refresh_token
    }
    return result
}

export function hasOAuth2Session(): boolean {
    hydrateAccessFromLocalStorage()
    return Boolean(memoryAccessToken || localStorage.getItem(TOKEN_KEY))
}

/** JWT access OAuth2 — нельзя писать только в легаси-ключ access_token. */
export function isOAuth2AccessJwt(token: string): boolean {
    if (!token || typeof token !== 'string' || token.split('.').length !== 3) {
        return false
    }
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        return payload.type === 'access'
    } catch {
        return false
    }
}

/**
 * Удаляет OAuth2 токены из памяти и localStorage
 */
export function removeOAuth2Tokens(): void {
    memoryAccessToken = null
    localStorage.removeItem(TOKEN_KEY)
    console.log('OAuth2 tokens removed from storage')
}

/**
 * Проверяет, истек ли access_token (извлекает время из JWT)
 */
export function isTokenExpired(): boolean {
    if (!hasOAuth2Session()) {
        return false
    }
    const tokens = getOAuth2TokensFromStorage()
    if (!tokens?.access_token) {
        return true
    }

    try {
        // Декодируем JWT токен без проверки подписи
        const payload = JSON.parse(atob(tokens.access_token.split('.')[1]))
        const expirationTime = payload.exp * 1000 // Конвертируем unix timestamp в миллисекунды
        const currentTime = new Date().getTime()

        return currentTime >= expirationTime
    } catch (error) {
        console.error('Failed to decode JWT token:', error)
        return false
    }
}

type RefreshAccessTokenOptions = {
    /** Если true — не чистим сессию здесь; это делает ensureValidToken / interceptor / loadSelf. */
    skipSessionClear?: boolean
}

/**
 * Обновляет access_token используя refresh_token
 */
export async function refreshAccessToken(
    props?: any,
): Promise<TokenRefreshResponse | null> {
    const options: RefreshAccessTokenOptions =
        props && typeof props === 'object' && 'skipSessionClear' in props
            ? props
            : { skipSessionClear: false }
    const sessionProps =
        props && typeof props === 'object' && !('skipSessionClear' in props) ? props : undefined

    const tokens = getOAuth2TokensFromStorage()
    const refreshFromLs = tokens?.refresh_token
    if (!refreshFromLs && !hasOAuth2Session()) {
        console.log('No refresh token available')
        return null
    }

    try {
        console.log('Attempting to refresh access token...')

        const response = await httpClient.post(
            '/user/refresh_token',
            refreshFromLs ? { refresh_token: refreshFromLs } : {},
            // Cookie (кабинет) или JSON (старые вкладки / партнёр). Негоняем interceptor в цикл.
            { skipAuthRefresh: true, withCredentials: true } as any,
        )

        const newTokens: TokenRefreshResponse = response.data
        const keepLsRefresh = isPersistRefreshSession()

        saveOAuth2Token({
            access_token: newTokens.access_token,
            refresh_token: keepLsRefresh
                ? (newTokens.refresh_token || refreshFromLs)
                : '',
        }, { persistRefresh: keepLsRefresh })

        console.log('Access token refreshed successfully')
        return newTokens

    } catch (error: any) {
        console.error('Failed to refresh access token:', error)

        // Logout только если явно передали app props (legacy) и не запретили clear
        if (
            !options.skipSessionClear &&
            error?.response?.status === 401 &&
            sessionProps
        ) {
            console.log('Refresh token expired, handling session expiration...')
            const { handleTokenRefreshFailure } = await import('./loadSelf')
            handleTokenRefreshFailure(sessionProps, 'refresh_endpoint_401')
        }

        return null
    }
}

/**
 * Проверяет, использует ли пользователь OAuth2
 */
export function isOAuth2User(): boolean {
    return hasOAuth2Session()
}

/**
 * Проверяет, истекает ли refresh_token в ближайшие дни (извлекает время из JWT)
 */
export function isRefreshTokenExpiringSoon(refreshToken: string, daysThreshold: number = 7): boolean {
    if (!refreshToken) return false

    try {
        // Декодируем JWT токен без проверки подписи
        const payload = JSON.parse(atob(refreshToken.split('.')[1]))
        const expirationTime = payload.exp * 1000 // Конвертируем unix timestamp в миллисекунды
        const currentTime = new Date().getTime()
        const daysUntilExpiry = (expirationTime - currentTime) / (1000 * 60 * 60 * 24)

        return daysUntilExpiry <= daysThreshold
    } catch (error) {
        console.error('Failed to decode refresh token:', error)
        return false
    }
}

/**
 * Проверяет статус токенов через API
 */
export async function checkTokenStatus(): Promise<any> {
    try {
        const response = await httpClient.get('/user/check_token_status')
        return response.data
    } catch (error) {
        console.error('Failed to check token status:', error)
        return null
    }
}
